"use client"

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion"
import { useDictionary } from "@/core/hooks/use-dictionary"
import { MainShopProduct } from "@/core/models/shop-product-model"
import { Plus } from "lucide-react"
import { useState } from "react"
import { getProductDisplayText } from "../../utils/productText"
import { formatWeightKg } from "@/utils/formatUtils"
import { BUSINESS_NAME } from '@/core/config/business';


export default function ProductDetail({ product }: { product: MainShopProduct }) {
    const { dictionary } = useDictionary()
    const [openItems, setOpenItems] = useState<string[]>([])

    const validMetaFields = product.meta_fields?.filter(
        (item) => item.value != null && item.value !== ""
    )

    const hasDimensions =
        (Number(product.fabric_area) !== 0 && product.fabric_area) ||
        (Number(product.weight) !== 0 && product.weight) ||
        product.overalsize

    const { detailsText } = getProductDisplayText(product)

    if (!detailsText && !hasDimensions && !validMetaFields?.length) return null

    const isOpen = (id: string) => openItems.includes(id)

    return (
        <div className="w-full border-t border-primary-100 p-4"

            style={{
                borderImageSlice: '4 4 4 4',
                borderImageWidth: '4px 4px 4px 4px',
                borderImageOutset: '.5px .5px .5px .5px',
                borderImageRepeat: 'round round',
                borderImageSource: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAVCAYAAACpF6WWAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAB3SURBVHgB7dVBCoAgFEXR911JjcRVVSurXamj2on5wiCkWd9RnpkiF0FRCSEkZCKyWWsXPHjvhzy/o5JSGp1zR7V2y2una3BHtbBnuEMo0u51Ba8NFLFn3u7hF+wZNPD3KB8HKNLudUWMcYUi9nj6MxSxJy3+qBOdU0PNJhRu4gAAAABJRU5ErkJggg==)',
                borderStyle: 'solid',
                imageRendering: 'pixelated'
            }}

        >
            <Accordion
                type="multiple"
                value={openItems}
                onValueChange={setOpenItems}
                className="w-full"
            >
                {/* توضیحات */}
                {detailsText && (
                    <AccordionItem value="description" className="border-b border-primary-100">
                        <AccordionTrigger className="group flex items-center justify-between py-6 hover:no-underline [&>svg]:hidden px-0">
                            <div className="flex items-center gap-4">
                                <span className="text-[10px] text-primary-600 font-light tabular-nums">
                                    01
                                </span>
                                <span className="text-sm text-primary-900">
                                    {dictionary.forms.description}
                                </span>
                            </div>
                            <div
                                className={`
                                    w-7 h-7 rounded-full border border-border/60 
                                    flex items-center justify-center
                                    transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)]
                                    ${isOpen("description")
                                        ? "rotate-45 bg-primary-900 border-primary-900"
                                        : "rotate-0 bg-transparent"
                                    }
                                `}
                            >
                                <Plus
                                    size={13}
                                    strokeWidth={1.5}
                                    className={`transition-colors duration-300 ${isOpen("description") ? "text-white" : "text-primary-500"}`}
                                />
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-8 pt-0 px-0">
                            <div className="pr-4 border-r-2 border-primary-100 mr-0">
                                <div
                                    className="text-sm text-primary-600 leading-loose"
                                    dangerouslySetInnerHTML={{ __html: detailsText }}
                                />
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                )}

                {/* ابعاد */}
                {hasDimensions && (
                    <AccordionItem value="dimensions" className="border-b border-primary-100">
                        <AccordionTrigger className="group flex items-center justify-between py-6 hover:no-underline [&>svg]:hidden px-0">
                            <div className="flex items-center gap-4">
                                <span className="text-[10px] text-primary-600">
                                    {detailsText ? "02" : "01"}
                                </span>
                                <span className="text-sm text-primary-900">
                                    {dictionary.forms.dimensions}
                                </span>
                            </div>
                            <div
                                className={`
                                    w-7 h-7 rounded-full border border-border/60 
                                    flex items-center justify-center
                                    transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)]
                                    ${isOpen("dimensions")
                                        ? "rotate-45 bg-primary-900 border-primary-900"
                                        : "rotate-0 bg-transparent"
                                    }
                                `}
                            >
                                <Plus
                                    size={13}
                                    strokeWidth={1.5}
                                    className={`transition-colors duration-300 ${isOpen("dimensions") ? "text-white" : "text-primary-500"}`}
                                />
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-8 pt-0 px-0">
                            <div className="grid grid-cols-3 gap-px divide-x divide-primary-100">
                                {Number(product.fabric_area) !== 0 && product.fabric_area && (
                                    <div className="flex flex-col justify-between p-2 bg-background">
                                        <span className="text-[10px] text-primary-600 mb-6">
                                            {dictionary.forms.fabric_area}
                                        </span>
                                        <span className="text-lg font-light text-primary-900 tabular-nums">
                                            {product.fabric_area}
                                        </span>
                                    </div>

                                )}
                                {Number(product.weight) !== 0 && product.weight && (
                                    <div className="flex flex-col justify-between p-2 bg-background">
                                        <span className="text-[10px] text-primary-600 mb-6">
                                            {dictionary.forms.weight}
                                        </span>
                                        <div className="flex items-end gap-1">
                                            <span className="text-lg font-light text-primary-900 tabular-nums">
                                                {BUSINESS_NAME === 'vanda' ? formatWeightKg(product.weight) : product.weight}
                                            </span>
                                            <span className="text-xs text-primary-600 mb-1">
                                                {dictionary.forms.kilogram}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {product.overalsize && (
                                    <div className="flex flex-col justify-between p-2 bg-background">
                                        <span className="text-[10px] text-primary-600 mb-6">
                                            {dictionary.forms.overalsize}
                                        </span>
                                        <span className="text-lg font-light text-primary-900">
                                            {product.overalsize}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                )}

                {/* مشخصات */}
                {validMetaFields?.length > 0 && (
                    <AccordionItem value="details" className="border-b border-primary-100">
                        <AccordionTrigger className="group flex items-center justify-between py-6 hover:no-underline [&>svg]:hidden px-0">
                            <div className="flex items-center gap-4">
                                <span className="text-[10px] text-primary-600 font-light tabular-nums">
                                    {[detailsText, hasDimensions].filter(Boolean).length === 2
                                        ? "03"
                                        : [detailsText, hasDimensions].filter(Boolean).length === 1
                                            ? "02"
                                            : "01"}
                                </span>
                                <span className="text-sm text-primary-900">
                                    {dictionary.ui.product.details.title}
                                </span>
                            </div>
                            <div
                                className={`
                                    w-7 h-7 rounded-full border border-border/60 
                                    flex items-center justify-center
                                    transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)]
                                    ${isOpen("details")
                                        ? "rotate-45 bg-primary-900 border-primary-900"
                                        : "rotate-0 bg-transparent"
                                    }
                                `}
                            >
                                <Plus
                                    size={13}
                                    strokeWidth={1.5}
                                    className={`transition-colors duration-300 ${isOpen("details") ? "text-white" : "text-primary-600"}`}
                                />
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-8 pt-0 px-0">
                            <div className="flex flex-col">
                                {validMetaFields.map((item, index) => (
                                    <div
                                        key={index}
                                        className="grid grid-cols-2 py-4 border-b border-border/20 last:border-0 group/row hover:bg-primary-50/50 transition-colors duration-200 px-2 -mx-2"
                                    >
                                        <span className="text-xs text-primary-600 tracking-wide">
                                            {item.label}
                                        </span>
                                        <span className="text-xs text-primary-900 text-left">
                                            {item.value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                )}
            </Accordion>
        </div>
    )
}
